import * as React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  Input,
  LinearProgress,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Sheet,
  Stack,
  Table,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  Textarea,
  Typography,
  Tooltip,
} from '@mui/joy';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import BookIcon from '@mui/icons-material/Book';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { useTheme } from '@mui/joy/styles';
import {
  fetchCongregationMasters,
  listAllBookReadings,
  listAllBookRecommendations,
  deleteBookRecommendation,
} from '../../api';
import type {
  CongregationBook,
  CongregationBookReading,
  CongregationBookRecommendation,
} from '../../types/congregation';
import { useSnackbarStore } from '../../store/useSnackbarStore';

interface BookReadingStats {
  bookId: number;
  bookTitle: string;
  bookAuthor?: string;
  totalReadings: number;
  readingStatus: {
    READING: number;
    COMPLETED: number;
    ABANDONED: number;
  };
}

export default function AdminBooksPage() {
  const snackbar = useSnackbarStore();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const heroBackground = isDark
    ? 'linear-gradient(180deg, rgba(15, 23, 42, 0.96) 0%, rgba(10, 15, 26, 0.98) 100%)'
    : 'radial-gradient(120% 140% at 8% 0%, rgba(255, 218, 170, 0.2) 0%, rgba(255, 218, 170, 0) 55%), radial-gradient(120% 140% at 95% 5%, rgba(165, 197, 255, 0.2) 0%, rgba(165, 197, 255, 0) 60%), linear-gradient(180deg, rgba(255, 252, 245, 1) 0%, rgba(248, 250, 255, 1) 45%, rgba(244, 246, 252, 1) 100%)';
  const heroOverlay = isDark
    ? 'radial-gradient(120% 120% at 15% -10%, rgba(56, 189, 248, 0.14), transparent 60%), radial-gradient(120% 120% at 100% 0%, rgba(251, 191, 36, 0.12), transparent 55%)'
    : 'radial-gradient(120% 120% at 15% -10%, rgba(255, 220, 160, 0.45), transparent 60%), radial-gradient(120% 120% at 100% 0%, rgba(129, 158, 255, 0.28), transparent 55%)';
  const heroTitle = isDark ? 'rgba(248,250,252,0.94)' : 'text.primary';
  const heroBody = isDark ? 'rgba(226,232,240,0.8)' : 'text.tertiary';
  const panelWarm = isDark
    ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.9), rgba(15, 23, 42, 0.95))'
    : 'linear-gradient(135deg, rgba(255, 237, 213, 0.9), rgba(255, 247, 231, 0.9))';
  const panelCool = isDark
    ? 'linear-gradient(135deg, rgba(15, 23, 42, 0.9), rgba(30, 41, 59, 0.95))'
    : 'linear-gradient(135deg, rgba(224, 243, 255, 0.8), rgba(241, 249, 255, 0.95))';
  const orbColor = isDark ? 'rgba(56, 189, 248, 0.18)' : 'rgba(255, 214, 153, 0.35)';
  const [activeTab, setActiveTab] = React.useState(0);
  const [books, setBooks] = React.useState<CongregationBook[]>([]);
  const [readings, setReadings] = React.useState<CongregationBookReading[]>([]);
  const [recommendations, setRecommendations] = React.useState<CongregationBookRecommendation[]>([]);
  const [stats, setStats] = React.useState<BookReadingStats[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [deleting, setDeleting] = React.useState(false);

  // Dialog states
  const [editDialog, setEditDialog] = React.useState<{
    open: boolean;
    record: CongregationBook | null;
  }>({ open: false, record: null });

  const [filterBatchId, setFilterBatchId] = React.useState<number | null>(null);
  const [filterMemberId, setFilterMemberId] = React.useState<number | null>(null);
  const readingSummary = React.useMemo(() => {
    const completed = readings.filter((reading) => reading.status === 'COMPLETED').length;
    const active = readings.filter((reading) => reading.status === 'READING').length;
    const abandoned = readings.filter((reading) => reading.status === 'ABANDONED').length;
    return {
      books: books.length,
      readings: readings.length,
      recommendations: recommendations.length,
      completed,
      active,
      abandoned,
    };
  }, [books.length, readings, recommendations.length]);

  React.useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const mastersData = await fetchCongregationMasters();
      setBooks(mastersData.books || []);

      // Load all readings and recommendations for statistics
      const readingsData = await listAllBookReadings();
      const recommendationsData = await listAllBookRecommendations();

      setReadings(readingsData || []);
      setRecommendations(recommendationsData || []);

      // Calculate statistics
      const statsMap = new Map<number, BookReadingStats>();
      (readingsData || []).forEach((reading) => {
        if (!statsMap.has(reading.bookId)) {
          const book = (mastersData.books || []).find((b) => b.id === reading.bookId);
          statsMap.set(reading.bookId, {
            bookId: reading.bookId,
            bookTitle: reading.bookTitle || 'Unknown Book',
            bookAuthor: reading.bookAuthor || undefined,
            totalReadings: 0,
            readingStatus: { READING: 0, COMPLETED: 0, ABANDONED: 0 },
          });
        }
        const stat = statsMap.get(reading.bookId)!;
        stat.totalReadings++;
        stat.readingStatus[reading.status as keyof typeof stat.readingStatus]++;
      });
      setStats(Array.from(statsMap.values()).sort((a, b) => b.totalReadings - a.totalReadings));
    } catch (error) {
      console.error('Failed to load books data:', error);
      snackbar.showSnackbar(error instanceof Error ? error.message : 'Failed to load books data', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRecommendation = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this recommendation?')) {
      return;
    }
    try {
      setDeleting(true);
      await deleteBookRecommendation(id);
      snackbar.showSnackbar('Recommendation deleted successfully', 'success');
      await loadData();
    } catch (error) {
      console.error('Failed to delete recommendation:', error);
      snackbar.showSnackbar(error instanceof Error ? error.message : 'Failed to delete recommendation', 'danger');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ py: { xs: 2.5, md: 4 }, width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
      <Stack spacing={{ xs: 2.5, md: 3.5 }} sx={{ maxWidth: 1200, mx: 'auto', width: '100%', px: { xs: 1.5, sm: 2, md: 0 } }}>
          <Card
            variant="outlined"
            sx={{
              position: 'relative',
              overflow: 'hidden',
              borderRadius: 'xl',
              borderColor: 'divider',
              boxShadow: 'lg',
              background: heroBackground,
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                background: heroOverlay,
                opacity: 0.9,
                pointerEvents: 'none',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                width: 180,
                height: 180,
                borderRadius: '50%',
                right: { xs: -60, md: -20 },
                top: { xs: 30, md: 10 },
                background: orbColor,
                filter: 'blur(6px)',
                animation: 'books-float 8s ease-in-out infinite',
                '@keyframes books-float': {
                  '0%': { transform: 'translateY(0px)' },
                  '50%': { transform: 'translateY(-12px)' },
                  '100%': { transform: 'translateY(0px)' },
                },
                pointerEvents: 'none',
              }}
            />
            <CardContent sx={{ position: 'relative' }}>
              <Stack spacing={2}>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', md: 'center' }}
                  spacing={2}
                  sx={{ minWidth: 0 }}
                >
                  <Stack spacing={0.75} sx={{ minWidth: 0 }}>
                  <Typography level="h1" sx={{ fontWeight: 800, letterSpacing: '-0.02em', color: heroTitle }}>
                    Books Management
                  </Typography>
                  <Typography level="body-sm" sx={{ color: heroBody, maxWidth: 640 }}>
                    Curate the congregation library, highlight reading momentum, and keep recommendations fresh.
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ minWidth: 0 }}>
                  <Chip variant="soft" color="primary">
                    {readingSummary.books} books
                  </Chip>
                    <Chip variant="soft" color="success">
                      {readingSummary.completed} completed
                    </Chip>
                    <Chip variant="soft" color="neutral">
                      {readingSummary.active} reading now
                    </Chip>
                  <Button startDecorator={<AddIcon />} variant="soft">
                    Add Book
                  </Button>
                </Stack>
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ minWidth: 0 }}>
                <Card
                  variant="soft"
                  sx={{
                    flex: 1,
                    borderRadius: 'lg',
                    background: panelWarm,
                    boxShadow: 'sm',
                    minWidth: 0,
                  }}
                >
                    <CardContent>
                      <Typography level="title-sm" sx={{ color: 'text.secondary' }}>
                        Reading activity
                      </Typography>
                      <Typography level="title-lg" sx={{ mt: 0.5 }}>
                        {readingSummary.readings} total readings
                      </Typography>
                      <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                        {readingSummary.abandoned} abandoned, {readingSummary.active} in progress.
                      </Typography>
                    </CardContent>
                  </Card>
                <Card
                  variant="soft"
                  sx={{
                    flex: 1,
                    borderRadius: 'lg',
                    background: panelCool,
                    boxShadow: 'sm',
                    minWidth: 0,
                  }}
                >
                    <CardContent>
                      <Typography level="title-sm" sx={{ color: 'text.secondary' }}>
                        Recommendations
                      </Typography>
                      <Typography level="title-lg" sx={{ mt: 0.5 }}>
                        {readingSummary.recommendations} live
                      </Typography>
                      <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                        Keep priorities aligned with upcoming programs.
                      </Typography>
                    </CardContent>
                  </Card>
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          <Tabs
            value={activeTab}
            onChange={(_, value) => value !== null && setActiveTab(value as number)}
            sx={{
              borderRadius: 'lg',
              backgroundColor: 'background.surface',
              boxShadow: 'sm',
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden',
            }}
          >
          <TabList
            sx={{
              px: 1,
              py: 1,
              gap: 1,
              flexWrap: 'wrap',
              bgcolor: 'transparent',
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
              <Tab sx={{ borderRadius: 'full', fontWeight: 600 }}>
                <BookIcon sx={{ mr: 1 }} />
                Reading Statistics
              </Tab>
              <Tab sx={{ borderRadius: 'full', fontWeight: 600 }}>
                <TrendingUpIcon sx={{ mr: 1 }} />
                All Readings ({readings.length})
              </Tab>
              <Tab sx={{ borderRadius: 'full', fontWeight: 600 }}>
                <BookIcon sx={{ mr: 1 }} />
                Recommendations ({recommendations.length})
              </Tab>
            </TabList>

            {/* Reading Statistics Tab */}
            <TabPanel value={0} sx={{ p: 3 }}>
              <Stack spacing={3}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                  <Typography level="title-lg">Book Reading Statistics</Typography>
                  <Chip variant="soft" color="primary">
                    {stats.length} tracked titles
                  </Chip>
                </Stack>

                {stats.length === 0 ? (
                  <Box sx={{ py: 4, textAlign: 'center' }}>
                    <Typography level="body-sm" color="neutral">
                      No reading data available yet
                    </Typography>
                  </Box>
                ) : (
                  <Grid container spacing={2}>
                    {stats.map((stat) => (
                      <Grid key={stat.bookId} xs={12} sm={6} md={4}>
                        <Card
                          variant="outlined"
                          sx={{
                            borderRadius: 'lg',
                            borderColor: 'divider',
                            boxShadow: 'sm',
                            transition: 'transform 0.2s, box-shadow 0.2s',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: 'lg',
                            },
                          }}
                        >
                          <CardContent>
                            <Stack spacing={1.5}>
                              <Stack spacing={0.5}>
                                <Typography level="title-md" noWrap>
                                  {stat.bookTitle}
                                </Typography>
                                {stat.bookAuthor && (
                                  <Typography level="body-xs" color="neutral">
                                    {stat.bookAuthor}
                                  </Typography>
                                )}
                              </Stack>

                              <Stack spacing={0.5}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                  <Typography level="body-sm">Total Readers</Typography>
                                  <Chip variant="soft" color="primary">
                                    {stat.totalReadings}
                                  </Chip>
                                </Stack>
                                <Stack spacing={0.5}>
                                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography level="body-xs">Currently Reading</Typography>
                                    <Typography level="body-xs" fontWeight={600}>
                                      {stat.readingStatus.READING}
                                    </Typography>
                                  </Stack>
                                  <LinearProgress
                                    determinate
                                    value={(stat.readingStatus.READING / stat.totalReadings) * 100}
                                    variant="soft"
                                    color="primary"
                                  />
                                </Stack>
                                <Stack spacing={0.5}>
                                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography level="body-xs">Completed</Typography>
                                    <Typography level="body-xs" fontWeight={600}>
                                      {stat.readingStatus.COMPLETED}
                                    </Typography>
                                  </Stack>
                                  <LinearProgress
                                    determinate
                                    value={(stat.readingStatus.COMPLETED / stat.totalReadings) * 100}
                                    variant="soft"
                                    color="success"
                                  />
                                </Stack>
                                <Stack spacing={0.5}>
                                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography level="body-xs">Abandoned</Typography>
                                    <Typography level="body-xs" fontWeight={600}>
                                      {stat.readingStatus.ABANDONED}
                                    </Typography>
                                  </Stack>
                                  <LinearProgress
                                    determinate
                                    value={(stat.readingStatus.ABANDONED / stat.totalReadings) * 100}
                                    variant="soft"
                                    color="danger"
                                  />
                                </Stack>
                              </Stack>
                            </Stack>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Stack>
            </TabPanel>

            {/* All Readings Tab */}
            <TabPanel value={1} sx={{ p: 3, overflow: 'auto' }}>
              <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                  <Typography level="title-lg">Member Book Readings</Typography>
                  <Chip variant="soft" color="neutral">
                    {readings.length} entries
                  </Chip>
                </Stack>

                {readings.length === 0 ? (
                  <Box sx={{ py: 4, textAlign: 'center' }}>
                    <Typography level="body-sm" color="neutral">
                      No readings recorded yet
                    </Typography>
                  </Box>
                ) : (
                  <Sheet
                    variant="outlined"
                    sx={{
                      borderRadius: 'lg',
                      overflow: 'auto',
                      boxShadow: 'sm',
                      borderColor: 'divider',
                    }}
                  >
                    <Table sx={{ width: '100%' }}>
                      <thead>
                        <tr>
                          <th style={{ width: '20%' }}>Member</th>
                          <th style={{ width: '25%' }}>Book Title</th>
                          <th style={{ width: '15%' }}>Author</th>
                          <th style={{ width: '10%' }}>Status</th>
                          <th style={{ width: '15%' }}>Started</th>
                          <th style={{ width: '15%' }}>Completed</th>
                        </tr>
                      </thead>
                      <tbody>
                        {readings.map((reading) => (
                          <tr key={reading.id}>
                            <td>{reading.memberName}</td>
                            <td>
                              <Typography level="body-sm" fontWeight={600}>
                                {reading.bookTitle}
                              </Typography>
                            </td>
                            <td>
                              <Typography level="body-xs" color="neutral">
                                {reading.bookAuthor || '-'}
                              </Typography>
                            </td>
                            <td>
                              <Chip
                                size="sm"
                                variant="soft"
                                color={
                                  reading.status === 'COMPLETED'
                                    ? 'success'
                                    : reading.status === 'ABANDONED'
                                      ? 'danger'
                                      : 'primary'
                                }
                              >
                                {reading.status}
                              </Chip>
                            </td>
                            <td>
                              <Typography level="body-xs">
                                {new Date(reading.startDate).toLocaleDateString()}
                              </Typography>
                            </td>
                            <td>
                              <Typography level="body-xs">
                                {reading.finishDate
                                  ? new Date(reading.finishDate).toLocaleDateString()
                                  : '-'}
                              </Typography>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Sheet>
                )}
              </Stack>
            </TabPanel>

            {/* Recommendations Tab */}
            <TabPanel value={2} sx={{ p: 3 }}>
              <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                  <Typography level="title-lg">Book Recommendations</Typography>
                  <Chip variant="soft" color="neutral">
                    {recommendations.length} active
                  </Chip>
                </Stack>

                {recommendations.length === 0 ? (
                  <Box sx={{ py: 4, textAlign: 'center' }}>
                    <Typography level="body-sm" color="neutral">
                      No recommendations created yet
                    </Typography>
                  </Box>
                ) : (
                  <Stack spacing={1}>
                    {recommendations.map((rec) => (
                      <Sheet
                        key={rec.id}
                        variant="soft"
                        sx={{
                          p: 2,
                          borderRadius: 'lg',
                          border: '1px solid',
                          borderColor: 'divider',
                          boxShadow: 'sm',
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                          <Stack spacing={0.5} sx={{ flex: 1 }}>
                            <Typography level="body-sm" fontWeight={600}>
                              {rec.bookTitle}
                            </Typography>
                            {rec.bookAuthor && (
                              <Typography level="body-xs" color="neutral">
                                by {rec.bookAuthor}
                              </Typography>
                            )}
                            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                              {rec.targetType === 'BATCH' ? (
                                <Chip size="sm" variant="soft">
                                  Recommended to Batch
                                </Chip>
                              ) : (
                                <Chip size="sm" variant="soft">
                                  Recommended to {rec.targetName}
                                </Chip>
                              )}
                              {rec.priority && (
                                <Chip
                                  size="sm"
                                  variant="outlined"
                                  color={rec.priority >= 4 ? 'danger' : 'neutral'}
                                >
                                  Priority: {rec.priority}/5
                                </Chip>
                              )}
                            </Stack>
                            {rec.message && (
                              <Typography level="body-xs" sx={{ mt: 1 }}>
                                "{rec.message}"
                              </Typography>
                            )}
                          </Stack>
                          <Tooltip title="Delete recommendation">
                            <IconButton
                              size="sm"
                              variant="plain"
                              color="danger"
                              onClick={() => handleDeleteRecommendation(rec.id)}
                              disabled={deleting}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Sheet>
                    ))}
                  </Stack>
                )}
              </Stack>
            </TabPanel>
          </Tabs>
      </Stack>

        {/* Edit Dialog - for future enhancement */}
        <Modal open={editDialog.open} onClose={() => setEditDialog({ open: false, record: null })}>
          <ModalDialog>
            <ModalClose variant="plain" sx={{ m: 1 }} />
            <Typography level="h4">Edit Book (Coming Soon)</Typography>
            <Typography level="body-sm" color="neutral" sx={{ mt: 1 }}>
              Book editing functionality will be available soon
            </Typography>
          </ModalDialog>
        </Modal>
    </Box>
  );
}
